import { useUserContext } from '../../../../contexts/UserContext';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { capitalize, get, includes, isEmpty, isNil, uniq } from 'lodash';
import { upsertOrRemoveCompetencies } from '../../../../api/domain/competency';
import { trigSurvey } from '../../../../support/qualarooTrigger';
import {
  REFLECT_COMPETENCY,
  REFLECT_SKILL
} from '../../../../constants/qualaroo';
import { updatePeopleSkills } from '../../../../api/domain/skill';
import { orderedCategory } from '../../../SkillsSelectPage/language';
import { updateProfile } from '../../../../actions/profile';
import FixedBar from '../../../../nonFeatureComponents/FixedBar';
import NavigationHeader from '../../../../nonFeatureComponents/NavigationHeader';
import StepperLine from '../../../../nonFeatureComponents/StepperLine';
import styles from './index.module.css';
import CategorySelector from '../../../../feature/ArchetypesSelector';
import SearchBar from '../../../../feature/SearchBar';
import CompetencyWarning from '../CompetencyWarning';
import ReflectList from '../ReflectList/ReflectList';
import CompetencyModal from '../../../../feature/SkillModal';
import PropTypes from 'prop-types';
import { reflectPageDes } from '../../language';
import ConfirmationModal from '../../../../feature/ConfirmationModal';

const NotSelectedCategory = -1;
const AllSelectedComIndex = 0;
export const ReflectContent = ({
  onGoTo,
  reflectType,
  updateItemFun,
  skills,
  currentSkills,
  getCurrentSkills,
  ...props
}) => {
  const {
    state: { user },
    dispatch
  } = useUserContext();

  const currentReflectInfo = reflectPageDes.reflectContent[reflectType];
  const {
    defaultFilterValue,
    categoryKey,
    emptyMessage,
    isShowSearchBar,
    steps,
    reflectPage: {
      reflectContentTitle,
      reflectContentSubTitle,
      warningDescription,
      warningTitle
    }
  } = currentReflectInfo;

  const [isFocus, setFocus] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [competency, setCompetency] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [isShowWarning, setIsShowWarning] = useState(
    user?.showReflectCompetencyWarning
  );
  const [filterSkills, setFilterSkills] = useState(currentSkills);
  const [categoryList, setCategoryList] = useState([defaultFilterValue]);
  const [categoryName, setCategoryName] = useState(defaultFilterValue);
  const [affectedSkills, setAffectedSkills] = useState([]);
  const [toBeRemovedSkills, setToBeRemovedSkills] = useState([]);

  //todo: tobe remove
  const isCompetency = reflectType === 'Competencies';
  const [
    isConfirmModalOpenAfterDone,
    setIsConfirmModalOpenAfterDone
  ] = useState(false);

  // Todo will be removed after removing <CompetencyWarning/>
  const [highlightedCompetency, setHighlightedCompetency] = useState('');

  const { confirmModalAfterDone } = reflectPageDes.reflectPage;

  useEffect(() => {
    const filteredCompetencies = skills.filter(item => item.toBeRemoved);
    setToBeRemovedSkills(filteredCompetencies);
    setAffectedSkills(filteredCompetencies);
  }, [skills]);

  useEffect(() => {
    if (!isEmpty(filterSkills)) {
      let categoryList = uniq(
        filterSkills.map(item => item[categoryKey])
      ).sort();

      //todo: tobe remove
      if (isCompetency) {
        const coreCompetencyType = [];
        filterSkills.forEach(item => {
          const coreCapabilities = item.coreCapabilities;
          coreCapabilities.forEach(item =>
            coreCompetencyType.push('Core competencies - ' + capitalize(item))
          );
        });
        categoryList = [defaultFilterValue]
          .concat(uniq(coreCompetencyType))
          .concat(categoryList);
      }

      if (!isCompetency) {
        const categories = [];
        orderedCategory.forEach(item => {
          if (includes(categoryList, item)) {
            categories.push(item);
          }
        });
        categoryList = [defaultFilterValue].concat(categories);
      }

      setCategoryList(categoryList);
      if (categoryList.indexOf(categoryName) === NotSelectedCategory) {
        setCategoryName(defaultFilterValue);
      }
    } else {
      setCategoryName(defaultFilterValue);
      setCategoryList([defaultFilterValue]);
    }
  }, [
    filterSkills,
    categoryName,
    categoryKey,
    defaultFilterValue,
    isCompetency
  ]);

  useEffect(() => {
    if (isFocus || !isEmpty(searchValue)) {
      setCategoryName(defaultFilterValue);
    }
  }, [isFocus, searchValue, defaultFilterValue]);

  const handleGoTo = useCallback(async () => {
    if (isUpdating) {
      return;
    }
    setIsUpdating(true);

    //todo:tobe remove
    if (isCompetency) {
      if (!isEmpty(currentSkills)) {
        await upsertOrRemoveCompetencies(
          currentSkills.map(com => ({
            id: get(com, 'reflectedInformation.id'),
            competencyId: com.competencyId,
            practiceLevel: get(com, 'reflectedInformation.practiceLevel', 0),
            isLearningPriority: get(
              com,
              'reflectedInformation.isLearningPriority',
              false
            )
          }))
        )
          .then(() => {
            onGoTo && onGoTo();
          })
          .finally(() => {
            setIsUpdating(false);
          });
      }
      localStorage.removeItem('newSelectedCompetencyName');
      trigSurvey(REFLECT_COMPETENCY);
    }
    if (!isCompetency) {
      if (!isEmpty(currentSkills)) {
        await updatePeopleSkills(
          currentSkills.map(skill => {
            return {
              id: get(skill, 'reflectedInformation.id'),
              skillId: skill.skillId,
              practiceLevel: get(
                skill,
                'reflectedInformation.practiceLevel',
                1
              ),
              isLearningPriority: get(
                skill,
                'reflectedInformation.isLearningPriority',
                false
              )
            };
          })
        )
          .then(() => {
            onGoTo && onGoTo();
          })
          .finally(() => {
            setIsUpdating(false);
          });
      }
      localStorage.removeItem('newSelectedSkillName');
      trigSurvey(REFLECT_SKILL);
    }
  }, [isUpdating, isCompetency, currentSkills, onGoTo]);

  const handleInfoClick = competency => {
    setCompetency(competency);
    setInfoOpen(true);
  };

  const onSearchBarChange = searchResult => {
    const { filterCoreDataList, curSearchVal } = searchResult;
    setSearchValue(curSearchVal);
    if (isEmpty(curSearchVal)) {
      setFilterSkills(currentSkills);
    } else {
      setFilterSkills(filterCoreDataList);
    }
  };

  const onSearchBarFocus = isFocus => {
    setFocus(isFocus);
  };

  const filterAffectedCompetencies = categoryName => {
    if (categoryName !== defaultFilterValue) {
      let filteredToBeRemovedCompetencies;
      if (categoryName.startsWith('Core competencies')) {
        const coreCapabilityName = categoryName.split(' - ')[1];
        filteredToBeRemovedCompetencies = toBeRemovedSkills.filter(item =>
          item.coreCapabilities.includes(coreCapabilityName)
        );
      } else {
        filteredToBeRemovedCompetencies = toBeRemovedSkills.filter(
          item => item.category === categoryName
        );
      }
      setAffectedSkills(filteredToBeRemovedCompetencies);
    }
  };

  const scrollView = competency => {
    const targetElement = document.querySelector(
      `[data-cy="reflectItem:${competency}"]`
    );
    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      setHighlightedCompetency(competency);

      setTimeout(() => {
        setHighlightedCompetency('');
      }, 1500);
    }
  };

  const affectedCompetenciesNames = affectedCompetencies => {
    affectedCompetencies = affectedCompetencies.map(item => item.name);

    return (
      <div>
        {affectedCompetencies.map((competency, index) => (
          <span key={index}>
            <span
              style={{
                textDecoration: 'underline',
                cursor: 'pointer'
              }}
              onClick={() => scrollView(competency)}
            >
              {competency}
            </span>
            <span>{index < affectedCompetencies.length - 1 && ', '}</span>
          </span>
        ))}
      </div>
    );
  };

  const closeWarning = () => {
    setIsShowWarning(false);
    updateProfile(dispatch)({ showReflectCompetencyWarning: false });
  };

  const haveEmptyCompetency = useMemo(() => {
    return (
      currentSkills.length > 0 &&
      currentSkills.some(
        com =>
          isNil(com.reflectedInformation) ||
          (com.reflectedInformation.practiceLevel === 0 &&
            com.reflectedInformation.isLearningPriority === false)
      )
    );
  }, [currentSkills]);

  const confirmModalInfoAfterDone = useMemo(() => {
    return (
      <>
        <div>{confirmModalAfterDone.content1}</div>
        <div>{confirmModalAfterDone.content2}</div>
      </>
    );
  }, [confirmModalAfterDone]);

  const toShowWarning = () => {
    const currentDate = new Date();
    const startDate = new Date('2023-06-01');
    const endDate = new Date('2023-12-31');
    return (
      isShowWarning &&
      currentDate >= startDate &&
      currentDate <= endDate &&
      affectedSkills.length > 0
    );
  };

  return (
    <>
      <FixedBar>
        <NavigationHeader
          onGoTo={
            haveEmptyCompetency
              ? () => setIsConfirmModalOpenAfterDone(true)
              : handleGoTo
          }
          backToTitle="Cancel"
          goToTitle={
            <span>
              I&#39;m do<u>n</u>e!
            </span>
          }
          disabled={isUpdating}
          {...props}
        >
          <StepperLine
            activeStep={1}
            steps={steps}
            isShowStepTooltip={isCompetency}
          />
        </NavigationHeader>
      </FixedBar>
      <div className={styles.page}>
        <div className={styles.titleWrapper}>
          <div>
            <h3 className={styles.title}>{reflectContentTitle}</h3>
            {reflectContentSubTitle && (
              <p className={styles.description}>{reflectContentSubTitle}</p>
            )}
            <section className={styles.reflectSearch}>
              <CategorySelector
                defaultOption={null}
                disabled={isFocus || !isEmpty(searchValue)}
                onChange={index => {
                  if (
                    index === AllSelectedComIndex ||
                    index === NotSelectedCategory
                  ) {
                    setCategoryName(defaultFilterValue);
                    setAffectedSkills(toBeRemovedSkills);
                  }
                  const categoryName = categoryList[index];
                  setCategoryName(categoryName);
                  filterAffectedCompetencies(categoryName);
                }}
                options={categoryList}
                value={categoryList.indexOf(categoryName)}
              />

              {isShowSearchBar && (
                <SearchBar
                  singleLayerArrayData={currentSkills}
                  searchConstants={reflectPageDes.searchBarConstants}
                  onSearchBarChange={onSearchBarChange}
                  onSearchBarFocus={onSearchBarFocus}
                />
              )}
            </section>
          </div>
        </div>
        {toShowWarning() && (
          <CompetencyWarning
            warningTitle={warningTitle}
            warningDescription={warningDescription}
            nameList={affectedCompetenciesNames(affectedSkills)}
            closeWarning={closeWarning}
          />
        )}
        <ReflectList
          handleNameClick={handleInfoClick}
          emptyMessage={emptyMessage}
          reflectType={reflectType}
          categoryName={categoryName}
          allCategoryList={categoryList}
          searchValue={searchValue}
          updateItemFun={updateItemFun}
          filterCompetencies={filterSkills}
          competencies={currentSkills}
          getCurrentCompetencies={getCurrentSkills}
          highlightedCompetency={highlightedCompetency}
        />
      </div>
      <CompetencyModal
        notShowSelectButton={true}
        skill={competency}
        open={infoOpen}
        close={() => setInfoOpen(false)}
      />
      <ConfirmationModal
        open={isConfirmModalOpenAfterDone}
        setOpen={open => {
          setIsConfirmModalOpenAfterDone(open);
        }}
        confirmButtonText={confirmModalAfterDone.confirmButtonText}
        cancelButtonText={confirmModalAfterDone.cancelButtonText}
        confirm={handleGoTo}
        cancel={() => {
          setIsConfirmModalOpenAfterDone(false);
        }}
        title={confirmModalAfterDone.title}
        content={confirmModalInfoAfterDone}
      />
    </>
  );
};

ReflectContent.propTypes = {
  onGoTo: PropTypes.func.isRequired,
  reflectType: PropTypes.string
};
